import { Text as RNText } from 'react-native';
import type { TextProps as RNTextProps, TextStyle } from 'react-native';

import { useTheme } from '../theme/ThemeProvider';
import type { ThemeTokens } from '../theme/tokens';

export type TextVariant = 'body' | 'muted' | 'caption' | 'label' | 'title' | 'heading';
export type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';

export interface TextProps extends RNTextProps {
  variant?: TextVariant;
  color?: string;
  weight?: TextWeight;
}

function variantStyle(theme: ThemeTokens, variant: TextVariant): TextStyle {
  switch (variant) {
    case 'muted':
      return { fontSize: theme.fontSize.md, color: theme.colors.textMuted };
    case 'caption':
      return { fontSize: theme.fontSize.xs, color: theme.colors.textMuted };
    case 'label':
      return { fontSize: theme.fontSize.sm };
    case 'title':
      return { fontSize: theme.fontSize.lg };
    case 'heading':
      return { fontSize: theme.fontSize.xl };
    case 'body':
    default:
      return { fontSize: theme.fontSize.md };
  }
}

const VARIANT_DEFAULT_WEIGHT: Record<TextVariant, TextWeight> = {
  body: 'regular',
  muted: 'regular',
  caption: 'regular',
  label: 'semibold',
  title: 'semibold',
  heading: 'bold',
};

function weightStyle(theme: ThemeTokens, variant: TextVariant, weight?: TextWeight): TextStyle {
  const effective = weight ?? VARIANT_DEFAULT_WEIGHT[variant];
  const fonts = theme.fontFamily;
  if (fonts !== undefined) {
    const family = variant === 'heading' || variant === 'title' ? fonts.heading : fonts[effective];
    return { fontFamily: family };
  }
  return { fontWeight: theme.fontWeight[effective] };
}

export function Text({ variant = 'body', color, weight, style, ...rest }: TextProps) {
  const theme = useTheme();
  return (
    <RNText
      style={[
        { color: theme.colors.text },
        variantStyle(theme, variant),
        weightStyle(theme, variant, weight),
        color !== undefined && { color },
        style,
      ]}
      {...rest}
    />
  );
}
